import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Box from "@mui/material/Box";
import { PaletteMode } from "@mui/material";
import { motion } from "framer-motion";
import { TrendingUp, Users, Shield } from "lucide-react";

import AppAppBar from "../../components/layout/AppAppBar";
import SecurityBadges from "../../components/landing/SecurityBadges";
import Footer from "../../components/layout/Footer";
import getLPTheme from "../../getLPTheme";

import EligibilityChecker from "../../components/reddit/EligibilityChecker";
import HowItWorksSection from "../../components/reddit/HowItWorksSection";
import FAQSection from "../../components/reddit/FAQSection";

export default function RedditLandingPage() {
  const [mode, setMode] = useState<PaletteMode>("light");
  const LPtheme = createTheme(getLPTheme(mode));

  const toggleColorMode = () => {
    setMode((prev) => (prev === "dark" ? "light" : "dark"));
  };

  return (
    <ThemeProvider theme={LPtheme}>
      <CssBaseline />
      <Helmet>
        <title>App20 Reddit Creator Program — Earn for Organic Sharing</title>
        <meta
          name="description"
          content="Join the App20 Reddit Creator Program. Turn your high-quality Reddit account (300+ Karma) into earnings. Get paid ₹100 per approved post directly to UPI."
        />
        <meta name="robots" content="index, follow" />
      </Helmet>

      <AppAppBar
        mode={mode}
        toggleColorMode={toggleColorMode}
        isBannerVisible={false}
        transparentMode={true}
        useTextLogo={false}
        simpleNavbar={true}
      />

      <Box sx={{ bgcolor: "#FFFFFF" }}>
        {/* Hero — cream gradient includes top padding so color shows under the navbar */}
        <Box
          sx={{
            width: "100%",
            background: "linear-gradient(180deg, #FFF3E0 0%, #FFF3E0 55%, #FFFFFF 100%)",
            pt: { xs: 12, sm: 14 },
            pb: { xs: 8, md: 12 },
            px: 2,
            position: "relative",
            overflow: "hidden",
          }}
        >
          <div className="max-w-3xl mx-auto relative z-10 text-center">
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              style={{
                fontFamily: "General-Sans-bold, sans-serif",
                fontSize: "clamp(2rem, 7vw, 3.75rem)",
                fontWeight: 800,
                lineHeight: 1.15,
                letterSpacing: "-0.5px",
                color: "#3F51B5",
                margin: 0,
              }}
            >
              Turn Your Reddit Karma{" "}
              <br className="hidden sm:inline" />
              Into{" "}
              <span style={{ color: "#FFB300" }}>Real Cash</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              style={{
                color: "#475569",
                fontSize: "clamp(0.95rem, 2.5vw, 1.125rem)",
                maxWidth: "36rem",
                margin: "1.5rem auto 0",
                lineHeight: 1.6,
                fontWeight: 600,
              }}
            >
              We pay active Reddit users with{" "}
              <strong style={{ color: "#3F51B5", fontWeight: 800 }}>300+ Karma</strong> to
              share recommendations and helpful posts for high-quality clients. Get paid ₹100
              instantly via UPI for every approved post.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="flex flex-col sm:flex-row items-center justify-center gap-4"
              style={{ paddingTop: "2rem" }}
            >
              <a
                href="#how-it-works"
                style={{
                  width: "100%",
                  maxWidth: "350px",
                  padding: "12px 32px",
                  borderRadius: "25px",
                  fontFamily: "General-Sans-bold, sans-serif",
                  fontWeight: 700,
                  fontSize: "1.05rem",
                  border: "2px solid #3F51B5",
                  color: "#3F51B5",
                  background: "transparent",
                  textDecoration: "none",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxSizing: "border-box",
                }}
              >
                How It Works
              </a>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5, duration: 0.8 }}
              className="grid grid-cols-3 gap-4 max-w-md mx-auto"
              style={{
                paddingTop: "1.5rem",
                marginTop: "2.5rem",
                borderTop: "1px solid #E2E8F0",
              }}
            >
              <div>
                <p
                  style={{
                    fontSize: "clamp(1.5rem, 4vw, 1.875rem)",
                    fontWeight: 900,
                    color: "#3F51B5",
                    margin: 0,
                    fontFamily: "General-Sans-bold, sans-serif",
                  }}
                >
                  ₹100
                </p>
                <p style={{ color: "#64748B", fontSize: 12, fontWeight: 700, marginTop: 2 }}>
                  Paid Per Post
                </p>
              </div>
              <div>
                <p
                  style={{
                    fontSize: "clamp(1.5rem, 4vw, 1.875rem)",
                    fontWeight: 900,
                    color: "#3F51B5",
                    margin: 0,
                    fontFamily: "General-Sans-bold, sans-serif",
                  }}
                >
                  300+
                </p>
                <p style={{ color: "#64748B", fontSize: 12, fontWeight: 700, marginTop: 2 }}>
                  Karma Needed
                </p>
              </div>
              <div>
                <p
                  style={{
                    fontSize: "clamp(1.5rem, 4vw, 1.875rem)",
                    fontWeight: 900,
                    color: "#3F51B5",
                    margin: 0,
                    fontFamily: "General-Sans-bold, sans-serif",
                  }}
                >
                  Instant
                </p>
                <p style={{ color: "#64748B", fontSize: 12, fontWeight: 700, marginTop: 2 }}>
                  UPI Payouts
                </p>
              </div>
            </motion.div>
          </div>

          <EligibilityChecker />
        </Box>

        <section className="py-8 border-y border-slate-200 bg-slate-50">
          <div className="max-w-7xl mx-auto px-4 flex flex-wrap justify-center items-center gap-8 md:gap-16 text-xs font-extrabold uppercase tracking-wider text-[#3F51B5]">
            <div className="flex items-center gap-2">
              <Shield className="w-4 h-4 text-[#FFC107]" />
              No Passwords Required
            </div>
            <div className="flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-[#FFC107]" />
              100% Organic Content
            </div>
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4 text-[#FFC107]" />
              2,500+ Active Creators
            </div>
          </div>
        </section>

        <HowItWorksSection />

        <section className="py-12 bg-indigo-50/30 border-t border-slate-200">
          <EligibilityChecker sectionId="checker-bottom" />
        </section>

        <Box
          sx={{
            background: "linear-gradient(180deg, #FFFFFF 0%, #FFFFFF 60%, #FFB300 100%)",
            pt: 0,
            pb: 0,
            mt: 0,
          }}
        >
          <SecurityBadges />
          <FAQSection />
          <Footer />
        </Box>
      </Box>
    </ThemeProvider>
  );
}
